import type { DmpAddressResponse, BuildingGroupEntry } from '@uniticket/shared';

interface AddressPickerTableProps {
  addresses: DmpAddressResponse[];
  selectedJvid: number | null;
  onSelect: (address: DmpAddressResponse) => void;
  buildingGroupEntries?: BuildingGroupEntry[];
  loadingVolumes?: boolean;
}

function renderExistingBoxes(value: string) {
  if (!value) return <span className="text-slate-400 italic text-xs">none</span>;
  const tokens = value.split(', ');
  return (
    <span className="font-mono text-xs">
      {tokens.map((t, i) => (
        <span key={i}>
          {i > 0 && <span className="text-slate-300">, </span>}
          {t.trim() === '' ? (
            <span className="italic text-slate-400">blank</span>
          ) : (
            <span className="text-slate-700">{t}</span>
          )}
        </span>
      ))}
    </span>
  );
}

export function AddressPickerTable({ addresses, selectedJvid, onSelect, buildingGroupEntries, loadingVolumes }: AddressPickerTableProps) {
  // Build lookup map: "postalCode|streetName|houseNumberExtension" → BuildingGroupEntry
  const entryMap = new Map<string, BuildingGroupEntry>();
  if (buildingGroupEntries) {
    for (const entry of buildingGroupEntries) {
      const key = `${entry.postalCode}|${entry.streetName}|${entry.houseNumberExtension}`;
      entryMap.set(key, entry);
    }
  }

  const showVolumes = buildingGroupEntries !== undefined;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <span className="text-sm font-semibold text-slate-700">
Select an address
        </span>
        <span className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
          {addresses.length} {addresses.length === 1 ? 'address' : 'addresses'}
        </span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <th className="text-left px-3 py-2">JVID</th>
              <th className="text-left px-3 py-2">Street</th>
              <th className="text-left px-3 py-2">House Nr</th>
              <th className="text-left px-3 py-2">Ext</th>
              <th className="text-left px-3 py-2">Bus</th>
              {showVolumes && <th className="text-left px-3 py-2 whitespace-nowrap">Vol MDU</th>}
              {showVolumes && <th className="text-left px-3 py-2 whitespace-nowrap">Exist. Boxes</th>}
              <th className="text-left px-3 py-2">Postal</th>
              <th className="text-left px-3 py-2">Municipality</th>
              <th className="text-left px-3 py-2">Type</th>
            </tr>
          </thead>
          <tbody>
            {addresses.map((addr) => {
              const isSelected = selectedJvid === addr.jvid;
              const addrKey = `${addr.postalCode ?? ''}|${addr.streetName ?? ''}|${addr.houseNumberExtension ?? ''}`;
              const matchedEntry = entryMap.get(addrKey);

              return (
                <tr
                  key={addr.jvid}
                  onClick={() => onSelect(addr)}
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-brand-rose/5 border-l-2 border-brand-rose'
                      : 'hover:bg-slate-50 border-l-2 border-transparent'
                  }`}
                >
                  <td className="px-3 py-2 font-mono text-xs font-medium text-slate-700">{addr.jvid}</td>
                  <td className="px-3 py-2 text-slate-700">{addr.streetName}</td>
                  <td className="px-3 py-2 text-slate-600">{addr.houseNumber ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{addr.houseNumberExtension ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{addr.busNumber ?? '—'}</td>
                  {showVolumes && (
                    <td className="px-3 py-2 font-mono text-xs">
                      {loadingVolumes ? (
                        <div className="h-4 w-10 bg-slate-200 rounded animate-pulse" />
                      ) : matchedEntry ? (
                        <span className="text-slate-700">{matchedEntry.volumeMdu}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  )}
                  {showVolumes && (
                    <td className="px-3 py-2">
                      {loadingVolumes ? (
                        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
                      ) : matchedEntry ? (
                        renderExistingBoxes(matchedEntry.existingBoxes)
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-slate-600 font-mono text-xs">{addr.postalCode}</td>
                  <td className="px-3 py-2 text-slate-600">{addr.municipality}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{addr.dwellingUnitType ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
